import {
  AssetTransaction,
  BuyingPower,
  CareExecutionReport,
  CareOrder,
  CustomerExecutionReport,
  CustomerOrder,
  CustomerQuote,
  CustomerTrade,
  ExecutionReport,
  FixingIndexRow,
  IndexMarkRow,
  MarketExecutionReport,
  MarketOrder,
  MarketQuote,
  MarketTrade,
  Order,
  Quote,
  Trade,
  type Column,
  type DateRangeFilter,
  type TabProps,
  type UsePersistedBlotterTable,
} from '@talos/kyoko';
import { NotificationMonitoringRow } from '../../Blotters/Notifications/NotificationMonitoringRow';

export const entityByRequestName = {
  Order: Order,
  Trade: Trade,
  Quote: Quote,
  ExecutionReport: ExecutionReport,
  MarketOrder: MarketOrder,
  MarketQuote: MarketQuote,
  MarketTrade: MarketTrade,
  MarketExecutionReport: MarketExecutionReport,
  CustomerOrder: CustomerOrder,
  CustomerQuote: CustomerQuote,
  CustomerExecutionReport: CustomerExecutionReport,
  CustomerTrade: CustomerTrade,
  AssetTransaction: AssetTransaction,
  BuyingPower: BuyingPower,
  CareOrder: CareOrder,
  CareExecutionReport: CareExecutionReport,
  Index: FixingIndexRow,
  IndexMark: IndexMarkRow,
  Notification: NotificationMonitoringRow,
} as const;

export type EntityName = keyof typeof entityByRequestName;

/** Panel type for a monitoring panel pinned to a single entity (e.g. 'orderMonitoring', 'tradeMonitoring') */
export type EntityMonitoringPanelType = `${Uncapitalize<EntityName>}Monitoring`;

const entityNames = Object.keys(entityByRequestName) as EntityName[];

export const ENTITY_MONITORING_PANEL_TYPES: readonly EntityMonitoringPanelType[] = entityNames.map(
  (name): EntityMonitoringPanelType =>
    `${name.charAt(0).toLowerCase()}${name.slice(1)}Monitoring` as EntityMonitoringPanelType
);

export type BlotterTableEntity = (typeof entityByRequestName)[EntityName];

const entityGroups = {
  OMS: 'OMS',
  Market: 'Market',
  Customer: 'Customer',
  Care: 'Care',
} as const;

interface EntityGroupAndDisplayName {
  group: (typeof entityGroups)[keyof typeof entityGroups] | undefined;
  name: string;
}
export const entityDisplayNames = {
  Order: { group: 'OMS', name: 'Orders' },
  Trade: { group: 'OMS', name: 'Trades' },
  Quote: { group: 'OMS', name: 'Quotes' },
  ExecutionReport: { group: 'OMS', name: 'Execution Reports' },
  MarketOrder: { group: 'Market', name: 'Market Orders' },
  MarketQuote: { group: 'Market', name: 'Market Quotes' },
  MarketTrade: { group: 'Market', name: 'Market Trades' },
  MarketExecutionReport: { group: 'Market', name: 'Market Execution Reports' },
  CustomerOrder: { group: 'Customer', name: 'Customer Orders' },
  CustomerQuote: { group: 'Customer', name: 'Customer Quotes' },
  CustomerExecutionReport: { group: 'Customer', name: 'Customer Execution Reports' },
  CustomerTrade: { group: 'Customer', name: 'Customer Trades' },
  CareOrder: { group: 'Care', name: 'Care Orders' },
  CareExecutionReport: { group: 'Care', name: 'Care Execution Reports' },
  AssetTransaction: { group: undefined, name: 'Cash Transactions' },
  Index: { group: undefined, name: 'Indices' },
  IndexMark: { group: undefined, name: 'Index Marks' },
  BuyingPower: { group: undefined, name: 'Buying Power' },
  Notification: { group: undefined, name: 'Notifications' },
} satisfies { [K in EntityName]: EntityGroupAndDisplayName };

export interface MonitoringBlotterTabProps extends TabProps {
  tag?: string;
  name?: EntityName;
  defaultFilter?: MonitoringBlotterTableFilter;
  defaultColumns?: Column[]; //(keyof Position | Partial<Column<any>>)[];
}
// This is the prefix for the actual monitoring tabs.
export const MONITORING_BLOTTER_PREFIX = 'monitoring-blotter';

export interface MonitoringBlotterTableFilter extends DateRangeFilter {
  Statuses?: string[];
  HideAPICalls?: boolean;
}

export interface UseMonitoringBlotterFilterParams {
  persistedBlotterTable: UsePersistedBlotterTable;
  selectedEntity: EntityName;
}
